import React, { useEffect } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "app/lib/store/store";
import {
  nextStep,
  prevStep,
  addStepsData,
} from "app/lib/store/slices/steps/jointAssetsLiabilities";
import { useAddAssetsLiabilities } from "app/hooks/assetsLiabilities/useAddAssetsLiabilities";
import MainTitle from "app/components/common/typography/MainTitle";
import ContinueButton from "app/components/common/buttons/Continue";
import PreviousButtom from "app/components/common/buttons/Previous";
import ProgressBar from "app/components/common/progressbar/ProgressBar";
import InfoTitle from "app/components/common/typography/InfoTitle";
import RadioButton from "app/components/common/buttons/RadioButton";
import AddMore from "app/components/common/buttons/AddMore";
import { ASSST_LIABILITY } from "app/constants/apiEndPoints";
import toast from "react-hot-toast";
import ErrorsMessage from "app/components/common/errors/ErrorsMessage";
import { JointAssetsLiabilitiesPartThreeFormData } from "app/utils/types";
import DynamicJointCarLaon from "app/components/Individual/applicant/assetsLiabilities/DynamicJointCarLaon";
import { useDeleteItem } from "app/hooks/useDeleteItem";

const CarLoan: React.FC = () => {
  const { mutate: deleteItem, isPending: deleteIsPending } = useDeleteItem();
  const dispatch = useDispatch();
  const savedIds = useSelector(
    (state: RootState) => state.loanTypesSteps?.savedIds
  );
  const { mutateAsync: submitAssetsLiabilities, isPending } =
    useAddAssetsLiabilities();

  const { formThree, assetLiabilityId, secoundAssetLiabilityId, ...others } =
    useSelector(
      (state: RootState) => state.jointAssetsLiabilities.stepsData
    ) || {
      formThree: { existingMortgage: [], carLoan: [] },
    };
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm<JointAssetsLiabilitiesPartThreeFormData>({
    defaultValues: {
      yourDetails: {
        carloanType: "no",
        carLoan: [
          {
            lenderName: "",
            balanceOwing: "",
            minMonthlyPayment: "",
            toBeRefinanced: false,
          },
        ],
      },
      coBorrowerDetails: {
        carloanType: "no",
        carLoan: [
          {
            lenderName: "",
            balanceOwing: "",
            minMonthlyPayment: "",
            toBeRefinanced: false,
          },
        ],
      },
    },
  });

  const carLoanValue = watch("yourDetails.carloanType");
  const coCarLoanValue = watch("coBorrowerDetails.carloanType");

  const {
    fields: carLoanFields,
    append: carLoanAppend,
    remove: carLoanRemove,
  } = useFieldArray({
    control,
    name: "yourDetails.carLoan",
  });

  const {
    fields: coCarLoanFields,
    append: coCarLoanAppend,
    remove: coCarLoanRemove,
  } = useFieldArray({
    control,
    name: "coBorrowerDetails.carLoan",
  });

  const onSubmit = async (data: JointAssetsLiabilitiesPartThreeFormData) => {
    const { yourDetails, coBorrowerDetails } = data;

    const yourDetailsPayload = {
      carLoan:
        yourDetails.carloanType === "yes"
          ? yourDetails?.carLoan?.map((item) => ({
              ...(item.dbId && { id: item.dbId }),
              lenderName: item.lenderName,
              toBeRefinanced: item.toBeRefinanced,
              balanceOwing: Number(item.balanceOwing),
              minMonthlyPayment: Number(item.minMonthlyPayment),
              noOfTitleHolders: Number(item.noOfTitleHolders),
            }))
          : null,
    };
    const coBorrowerDetailsPayload = {
      carLoan:
        coBorrowerDetails.carloanType === "yes"
          ? coBorrowerDetails?.carLoan?.map((item) => ({
              ...(item.dbId && { id: item.dbId }),
              lenderName: item.lenderName,
              toBeRefinanced: item.toBeRefinanced,
              balanceOwing: Number(item.balanceOwing),
              minMonthlyPayment: Number(item.minMonthlyPayment),
              noOfTitleHolders: Number(item.noOfTitleHolders),
            }))
          : null,
    };

    const request1 = {
      url: `${ASSST_LIABILITY}/${savedIds.applicantID}/fourth`,
      data: yourDetailsPayload,
    };
    const request2 = {
      url: `${ASSST_LIABILITY}/${savedIds.secondApplicantID}/fourth`,
      data: coBorrowerDetailsPayload,
    };
    try {
      const [response1, response2] = await Promise.all([
        submitAssetsLiabilities(request1),
        submitAssetsLiabilities(request2),
      ]);
      if (response1 && response2) {
        const yourCarLoanMapped = response1?.carLoan?.map((item: any) => ({
          dbId: item.id,
          lenderName: item.lenderName,
          balanceOwing: item.balanceOwing,
          minMonthlyPayment: item.minMonthlyPayment,
          toBeRefinanced: item.toBeRefinanced,
          noOfTitleHolders: String(item.noOfTitleHolders),
        }));
        const coCarLoanMapped = response2?.carLoan?.map((item: any) => ({
          dbId: item.id,
          lenderName: item.lenderName,
          balanceOwing: item.balanceOwing,
          minMonthlyPayment: item.minMonthlyPayment,
          toBeRefinanced: item.toBeRefinanced,
          noOfTitleHolders: String(item.noOfTitleHolders),
        }));
        const savedData = {
          yourDetails: {
            ...formThree?.yourDetails,
            carLoan: yourDetails.carloanType === "no" ? [] : yourCarLoanMapped,
            carloanType: yourDetails.carloanType,
          },
          coBorrowerDetails: {
            ...formThree?.coBorrowerDetails,
            carLoan:
              coBorrowerDetails.carloanType === "no" ? [] : coCarLoanMapped,
            carloanType: coBorrowerDetails.carloanType,
          },
        };
        dispatch(
          addStepsData({
            ...others,
            formThree: savedData,
          })
        );
        dispatch(nextStep());
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Something went wrong.");
    }
  };

  useEffect(() => {
    if (formThree && Object.keys(formThree).length > 0) {
      reset({
        yourDetails: {
          ...formThree.yourDetails,
          carLoan:
            formThree.yourDetails?.carloanType === "no"
              ? [
                  {
                    lenderName: "",
                    balanceOwing: "",
                    minMonthlyPayment: "",
                    noOfTitleHolders: "",
                    toBeRefinanced: false,
                  },
                ]
              : formThree.yourDetails.carLoan?.map((item: any) => ({
                  ...item,
                  noOfTitleHolders: String(item?.noOfTitleHolders ?? ""),
                })) || [
                  {
                    lenderName: "",
                    balanceOwing: "",
                    minMonthlyPayment: "",
                    noOfTitleHolders: "",
                    toBeRefinanced: false,
                  },
                ],
        },
        coBorrowerDetails: {
          ...formThree.coBorrowerDetails,
          carLoan:
            formThree.coBorrowerDetails?.carloanType === "no"
              ? [
                  {
                    lenderName: "",
                    balanceOwing: "",
                    minMonthlyPayment: "",
                    noOfTitleHolders: "",
                    toBeRefinanced: false,
                  },
                ]
              : formThree.coBorrowerDetails.carLoan?.map((item: any) => ({
                  ...item,
                  noOfTitleHolders: String(item?.noOfTitleHolders ?? ""),
                })) || [
                  {
                    lenderName: "",
                    balanceOwing: "",
                    minMonthlyPayment: "",
                    noOfTitleHolders: "",
                    toBeRefinanced: false,
                  },
                ],
        },
      });
    }
  }, [formThree, reset]);

  return (
    <>
      <ProgressBar progress={48} MB={4} />
      <div className="w-full sm:w-[60%] lg:w-[50%] mx-auto text-center px-4 sm:px-0">
        <MainTitle title="Car loan" />
      </div>
      <div className="mx-auto w-internalWidth">
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex mb-8 flex-col md:flex-col xl:flex-row sm:gap-4">
            <div className="flex-1">
              <InfoTitle title="Your Details" />
              <div className="text-left mb-5">
                <h6 className="font-Mulish text-nowrap text-base text-[#585C7B] mb-4">
                  Do you have any personal/car loan?
                </h6>
                <div className="flex gap-5">
                  <Controller
                    name="yourDetails.carloanType"
                    control={control}
                    rules={{ required: "Please select an option" }}
                    render={({ field: { value, onChange } }) => (
                      <div className="flex gap-5">
                        <RadioButton
                          id="carloan_yes"
                          name="yourDetails.carloanType"
                          value="yes"
                          label="Yes"
                          checked={value === "yes"}
                          onChange={onChange}
                        />
                        <RadioButton
                          id="carloan_no"
                          name="yourDetails.carloanType"
                          value="no"
                          label="No"
                          checked={value === "no"}
                          onChange={onChange}
                        />
                      </div>
                    )}
                  />
                </div>
                {errors?.yourDetails?.carloanType && (
                  <ErrorsMessage
                    title={errors.yourDetails.carloanType.message}
                    className="!text-left"
                  />
                )}
              </div>

              {carLoanValue === "yes" && (
                <>
                  <DynamicJointCarLaon
                    control={control}
                    errors={errors}
                    fields={carLoanFields}
                    deleteItem={deleteItem}
                    carLoan="yourDetails.carLoan"
                    applicantID={savedIds?.applicantID as string}
                    remove={carLoanRemove}
                    deleteIsPending={deleteIsPending}
                  />
                  {carLoanFields.length < 3 && (
                    <div className="flex items-center justify-end mt-6 mr-0   mb-6 sm:mb-2">
                      <AddMore
                        onClick={() =>
                          carLoanAppend({
                            lenderName: "",
                            balanceOwing: "",
                            minMonthlyPayment: "",
                            noOfTitleHolders: "",
                            toBeRefinanced: false,
                          })
                        }
                      />
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="flex-1">
              <InfoTitle title="Co-borrower" />
              <div className="text-left mb-5">
                <h6 className="font-Mulish text-nowrap text-base text-[#585C7B] mb-4">
                  Do you have any personal/car loan?
                </h6>
                <div className="flex gap-5">
                  <Controller
                    name="coBorrowerDetails.carloanType"
                    control={control}
                    rules={{ required: "Please select an option" }}
                    render={({ field: { value, onChange } }) => (
                      <div className="flex gap-5">
                        <RadioButton
                          id="cocarloan_yes"
                          name="coBorrowerDetails.carloanType"
                          value="yes"
                          label="Yes"
                          checked={value === "yes"}
                          onChange={onChange}
                        />
                        <RadioButton
                          id="cocarloan_no"
                          name="coBorrowerDetails.carloanType"
                          value="no"
                          label="No"
                          checked={value === "no"}
                          onChange={onChange}
                        />
                      </div>
                    )}
                  />
                </div>
                {errors?.coBorrowerDetails?.carloanType && (
                  <ErrorsMessage
                    title={errors.coBorrowerDetails.carloanType.message}
                    className="!text-left"
                  />
                )}
              </div>

              {coCarLoanValue === "yes" && (
                <>
                  <DynamicJointCarLaon
                    control={control}
                    errors={errors}
                    fields={coCarLoanFields}
                    deleteItem={deleteItem}
                    carLoan="coBorrowerDetails.carLoan"
                    applicantID={savedIds?.secondApplicantID as string}
                    remove={coCarLoanRemove}
                    deleteIsPending={deleteIsPending}
                  />
                  {coCarLoanFields.length < 3 && (
                    <div className="flex items-center justify-end mt-6 mr-0">
                      <AddMore
                        onClick={() =>
                          coCarLoanAppend({
                            lenderName: "",
                            balanceOwing: "",
                            minMonthlyPayment: "",
                            noOfTitleHolders: "",
                            toBeRefinanced: false,
                          })
                        }
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="flex justify-between mb-6">
            <PreviousButtom
              onClick={() => {
                dispatch(prevStep());
              }}
            >
              Previous
            </PreviousButtom>
            <ContinueButton type="submit" loading={isPending}>
              Next
            </ContinueButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default CarLoan;
